import { queryOptions } from "@tanstack/react-query";
import { fetchProfile } from "../../api/accounts";
import { QueryKeys } from "../../queryKeys";
import type { JobTypeName, Profile } from "../../types";

type Role = "user" | "admin" | "engineer";

const normalizeRole = (role?: string): Role => {
  if (!role) return "user";
  const normalized = role?.toString()?.trim().toLowerCase();
  if (normalized === "admin" || normalized === "engineer" || normalized === "user") {
    return normalized;
  }
  return "user";
};

export type ProfileView = Profile & {
  role: Role;
  allowedJobTypes: JobTypeName[];
  isAdmin: boolean;
  isEngineer: boolean;
  canRunCalculations: boolean;
  canViewCostAndMargin: boolean;
  isMechanicalOnly: boolean;
};

export const profileSelectors = {
  role: (response: Profile) => normalizeRole(response.role),
  allowedJobTypes: (response: Profile) => response.allowed_job_types ?? [],
  isAdmin: (response: Profile) => normalizeRole(response.role) === "admin",
  isEngineer: (response: Profile) => normalizeRole(response.role) === "engineer",
  canRunCalculations: (response: Profile) => normalizeRole(response.role) === "engineer",
  canViewCostAndMargin: (response: Profile) => normalizeRole(response.role) === "admin",
  isMechanicalOnly: (response: Profile) => {
    const allowedJobTypes = response.allowed_job_types ?? [];
    return allowedJobTypes.length === 1 && allowedJobTypes[0] === "mechanical";
  },
  summary: (response: Profile): ProfileView => {
    const role = normalizeRole(response.role);
    const allowedJobTypes = response.allowed_job_types ?? [];
    const isAdmin = role === "admin";
    const isEngineer = role === "engineer";
    const canRunCalculations = isEngineer;
    const canViewCostAndMargin = isAdmin;
    const isMechanicalOnly = allowedJobTypes.length === 1 && allowedJobTypes[0] === "mechanical";
    return {
      ...response,
      role,
      allowedJobTypes,
      isAdmin,
      isEngineer,
      canRunCalculations,
      canViewCostAndMargin,
      isMechanicalOnly,
    };
  },
};

export const selectProfileView = profileSelectors.summary;

export const profileQueryOptions = () =>
  queryOptions({
    queryKey: [QueryKeys.Profile],
    queryFn: fetchProfile,
  });
